import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Building2, Shield, FileText } from 'lucide-react';

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Header */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/estate-flow-logo.png" 
                alt="Estate Flow Logo" 
                className="h-14 w-auto"
              />
              <div>
                <span className="text-2xl font-bold text-gray-900">Estate Flow</span>
                <p className="text-xs text-gray-500 -mt-1">Where Property Meets Simplicity</p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-emerald-100 rounded-full">
                <FileText className="h-12 w-12 text-emerald-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
            <p className="text-gray-600">Sebastian Alliance Group LLC</p>
            <p className="text-sm text-gray-500 mt-2">Last Updated: December 3, 2024</p>
          </div>

          {/* Content */}
          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-emerald-600 mr-2" />
                1. Agreement to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions ("Terms") constitute a legally binding agreement between you and <strong>Sebastian Alliance Group LLC</strong> ("Company", "we", "us", or "our") concerning your access to and use of the <strong>Estate Flow</strong> property management platform (the "Service").
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. About Estate Flow</h2>
              <p className="text-gray-700 leading-relaxed">
                Estate Flow is a proprietary digital product developed and owned by <strong>Sebastian Alliance Group LLC</strong>. The platform provides comprehensive property management solutions for landlords, property managers, HOA administrators, and tenants.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">3.1 Registration</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To use the Service, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">3.2 Account Security</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Payment Terms</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">4.1 Payment Processing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All payments are processed by Sebastian Alliance Group LLC through our secure payment gateway. We accept Visa and MasterCard through our licensed payment processors. Payouts are facilitated via our global payout network, supporting major US banks and international financial institutions in all authorized regions.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">4.2 Subscription Fees</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Subscription fees are billed in advance on a monthly or annual basis, depending on your chosen plan. All fees are non-refundable except as required by law or as explicitly stated in these Terms.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">4.3 Price Changes</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We reserve the right to change our fees at any time. We will provide you with at least 30 days' notice of any fee increase. Your continued use of the Service after the fee increase takes effect constitutes your acceptance of the new fees.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service and all content, features, and functionality are owned by Sebastian Alliance Group LLC and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Estate Flow™ is a registered trademark of Sebastian Alliance Group LLC. You may not use our trademarks without our prior written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Prohibited Activities</h2>
              <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Use the Service for any illegal purpose or in violation of any applicable laws</li>
                <li>Attempt to gain unauthorized access to the Service or its related systems</li>
                <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
                <li>Use any robot, spider, or other automatic device to access the Service</li>
                <li>Transmit any viruses, malware, or other malicious code</li>
                <li>Collect or harvest any personally identifiable information from the Service</li>
                <li>Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Protection and Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy, which describes how we collect, use, and disclose information about you.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We implement appropriate technical and organizational security measures to protect your personal data against unauthorized or unlawful processing and against accidental loss, destruction, or damage.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SEBASTIAN ALLIANCE GROUP LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Sebastian Alliance Group LLC and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will immediately cease.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States and the state in which Sebastian Alliance Group LLC is registered, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. If we make material changes to these Terms, we will notify you by email or by posting a notice on the Service prior to the effective date of the changes. Your continued use of the Service after the effective date constitutes your acceptance of the revised Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Sebastian Alliance Group LLC</strong>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  For questions about these Terms, please contact us at:
                </p>
                <p className="text-gray-700 leading-relaxed mt-2">
                  Email: compliance@sebasalliance.net
                </p>
              </div>
            </section>

            <div className="mt-12 p-6 bg-emerald-50 border-l-4 border-emerald-600 rounded">
              <p className="text-sm text-gray-700">
                By using Estate Flow, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
